import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MultiSelectItem, MultiSelectOptions } from '@usitsdasdesign/dds-ng/multi-select';
import { AdminConstant } from '../../constants/admin-constants';
import { TagOptions } from '@usitsdasdesign/dds-ng/tags';

@Component({
  selector: 'app-admin-nested-multi-select',
  templateUrl: './admin-nested-multi-select.component.html',
  styleUrl: './admin-nested-multi-select.component.less',
})
export class AdminNestedMultiSelectComponent implements OnInit, OnChanges {

  // Input properties to receive data from parent components
  @Input() applicableFssItems: MultiSelectItem[] = [];
  @Input() applicableIndustryItems: MultiSelectItem[] = [];
  @Input() applicableIndustrySectorItems: MultiSelectItem[] = [];
  @Input() excludedIndustrySectorCodes: { [key: string]: string[] } = {};
  @Input() isEditMode: boolean = false;
  @Input() selectedEmpId: string | null = null;
  @Input() selectedFSSTypes: string[] = [];
  @Input() selectedIndustrySectorCodes: { [key: string]: string[] } = {};
  @Output() selectedFssCodesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedIndustryCodesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedSectorCodesChange: EventEmitter<any> = new EventEmitter<any>();

  peiCode = 101; // PEI code constant
  peiCodeString = '101'; // PEI code string constant
  peiSectorName = 'PEI'; // PEI sector name constant

  // Multi-select options for FSS, Industry, and Sector
  fssMultiSelectOptions: MultiSelectOptions = { ...AdminConstant.MultiSelectOptions };
  industryMultiSelectOptions: MultiSelectOptions = { ...AdminConstant.MultiSelectOptions };
  sectorMultiSelectOptions: MultiSelectOptions = { ...AdminConstant.MultiSelectOptions };
  tagOptions: TagOptions = { ...AdminConstant.TagOptions };

  // Dropdown options for FSS, Industry, and Sector
  fssOptions: MultiSelectItem[] = [];
  industryOptions: { [key: string]: AdminNestedData[] } = {};
  sectorOptions: { [key: string]: AdminNestedData[] } = {};

  // State management for selected items
  selectedFSS: AdminNestedData[] = [];
  selectedIndustries: { [key: string]: AdminNestedData[] } = {};
  selectedSectors: { [key: string]: AdminNestedData[] } = {};

  // Lifecycle hook: Called once the component is initialized
  ngOnInit(): void { }

  // Lifecycle hook: Called whenever input properties change
  ngOnChanges(changes: SimpleChanges): void {
    // Initialize FSS options
    this.fssOptions = this.applicableFssItems;
    this.industryOptions = {};
    this.sectorOptions = {};

    // Populate selected FSS items based on input
    if (this.selectedEmpId && this.isEditMode && this.selectedFSSTypes.length > 0) {
      this.selectedFSS = this.applicableFssItems
        .filter((fss) => this.selectedFSSTypes.includes(fss['itemCode']))
        .map((fss) => ({
          label: fss.label,
          val: fss['itemCode'] || '',
          value: fss.value,
          itemId: fss['itemId'],
          itemCode: fss['itemCode']
        } as AdminNestedData));
    }

    // Process applicable FSS items
    this.applicableFssItems.map((item: any) => {
      const selectedIndustrySectorCodes = this.selectedIndustrySectorCodes[item.itemCode] || [];
      selectedIndustrySectorCodes.map((code) => {
        if (code) {
          const sectorCode = code;
          const industryCode = code.split('_')[0];
          if (this.selectedEmpId && this.isEditMode) {
            // Handle industry changes
            this.onIndustryChangeInit(
              this.applicableIndustryItems
                .filter((industry) => industry['itemId'] === Number(industryCode))
                .map((industry) => ({
                  label: industry.label,
                  val: industry['itemId'] || '',
                  itemId: industry['itemId'],
                  itemCode: item.itemCode,
                  value: true
                } as AdminNestedData)),
              item.itemCode
            );

            // Handle sector changes
            this.onSectorChangeInit(
              this.applicableIndustrySectorItems
                .filter((sector) => sector['itemCode'] === sectorCode)
                .map((sector) => ({
                  label: sector['heading'],
                  val: sector['itemCode'] || '',
                  itemId: sector['itemId'],
                  itemCode: sector['itemCode'],
                  value: sector.value
                } as AdminNestedData)),
              industryCode,
              item.itemCode
            );
          }
        }
      });

      // Populate industry options
      this.industryOptions[String(item.itemCode)] = this.applicableIndustryItems.map((industry: any) => ({
        label: industry.label,
        itemCode: item.itemCode,
        itemId: industry.itemId,
        val: industry.itemId || '',
        fssCode: item.itemCode,
      } as AdminNestedData));

      // Filter out PEI industry from the list for 1_DC fss
      if(item.itemCode === '1_DC') {
        this.industryOptions[String(item.itemCode)] = this.industryOptions[String(item.itemCode)].filter((industry) => Number(industry['itemId']) !== this.peiCode);
      }

      // Populate sector options for each industry
      this.industryOptions[String(item.itemCode)].map((industry: any) => {
        this.sectorOptions[String(industry.itemId)+'_'+ item.itemCode] = this.applicableIndustrySectorItems
          .filter((x) => Number(x.label) === industry.itemId)
          .map((sector: any) => ({
            label: sector.heading,
            itemId: industry.itemId,
            val: sector.itemCode || '',
            itemCode: item.itemCode,
            fssCode: item.itemCode,
          } as AdminNestedData));
      });
    });

    // Remove excluded sectors for the current FSS
    Object.entries(this.excludedIndustrySectorCodes).forEach(([key, value]) => {
      const fssCode = key;
      const excludedSectors = value;
      excludedSectors.forEach((sectorCode) => {
        if(sectorCode && sectorCode !== 'null') {
        const industryCode = sectorCode === this.peiCodeString ? this.peiCodeString : sectorCode.split('_')[0];
        const sectorKey = industryCode+'_'+fssCode;
        this.sectorOptions[sectorKey] = this.sectorOptions[sectorKey]?.filter((sector) => sector['val'] !== sectorCode);
        this.industryOptions[fssCode] = this.industryOptions[fssCode]?.filter((industry) => this.sectorOptions[industry.itemId+'_'+fssCode]?.length > 0);
        }
      });
    });

    // Remove FSS options that don't have industry options available
    this.fssOptions = this.fssOptions.filter((fss) => {
      const industryOptionsForFss = this.industryOptions[fss['itemCode']];
      return industryOptionsForFss && industryOptionsForFss.length > 0;
    });

    // Remove selected sectors when no match is found in sectorOptions
    Object.keys(this.selectedSectors).forEach((key) => {
      const validSectors = this.sectorOptions[key]?.map((sector) => sector.val) || [];
      this.selectedSectors[key] = this.selectedSectors[key]?.filter((sector) => validSectors.includes(sector.val)) || [];
    });

    // Remove selected industries when no match is found in industryOptions
    Object.keys(this.selectedIndustries).forEach((key) => {
      const validIndustries = this.industryOptions[key]?.map((industry) => industry.val) || [];
      this.selectedIndustries[key] = this.selectedIndustries[key]?.filter((industry) => validIndustries.includes(industry.val)) || [];
    });

    // Remove selected FSS when no match is found in fssOptions
    this.selectedFSS = this.selectedFSS.filter((fss) => {
      const fssCode = fss.itemCode;
      return this.fssOptions.some((option) => option['itemCode'] === fssCode) && this.selectedIndustries[String(fssCode)]?.length > 0;
    });

    if(this.selectedEmpId && this.isEditMode) {
      this.emitFssChange();
      this.emitIndustryChange();
      this.emitSectorChange();
    }
    
    // Trigger FSS change handler
    this.onFSSChange(this.selectedFSS);
  }

  // Handler for FSS changes
  onFSSChange(selected: any) {
    this.selectedFSS = selected;
    this.emitFssChange();
    // Handle unselected FSS items
    const unselectedFSS = this.fssOptions.filter(
      (fss) => !this.selectedFSS.some((selected) => selected.itemCode === fss['itemCode'])
    );
    unselectedFSS.map((fss) => {
      if (fss.value) {
        const industries = this.selectedIndustries[fss['itemCode'] ?? ''];
        industries?.map((industry) => this.removeIndustry(fss['itemCode'] ?? '', industry.val));
      }
    });
  }

  // Handler for industry changes
  onIndustryChange(selected: any, fssVal: string) {
    this.selectedIndustries[fssVal] = selected;
    // Handle PEI Industry selection
    this.handlePEIIndustryAndSector(fssVal);
    this.emitIndustryChange();
  }

  private handlePEIIndustryAndSector(fssVal: string) {
    const peiIndustry = this.selectedIndustries[fssVal]?.find((v: any) => v.val === this.peiCode);
    if (peiIndustry) {
      const sectorKey = this.peiCodeString+'_'+fssVal;
      const peiSector = this.sectorOptions[sectorKey]?.find((v) => v.val === this.peiCodeString);
      if (!peiSector) {
        const peiSectorItem = { label: this.peiSectorName, val: this.peiCodeString, itemId: this.peiCodeString, value: true, itemCode: this.peiCodeString };
        if (this.sectorOptions[sectorKey] == null) {
          this.sectorOptions[sectorKey] = [];
        }
        this.sectorOptions[sectorKey] = [peiSectorItem, ...this.sectorOptions[sectorKey]];

        const peiSectorOption = this.applicableIndustrySectorItems.find((x) => x['itemCode'] === this.peiCodeString);
        if (!peiSectorOption) {
          this.applicableIndustrySectorItems.push({
            description: "",
            disabled: false,
            globalClientCode: null,
            heading: this.peiSectorName,
            isItemActive: true,
            itemCode: this.peiCodeString,
            itemId: this.peiCode,
            label: this.peiCodeString,
            value: true,
            val: this.peiCodeString,
          });
        }
      }
    }
  }

  // Handler for sector changes
  onSectorChange(selected: any, industryVal: string, fssVal: string) {
    const compositeKey = industryVal + '_' +  fssVal;  
    this.selectedSectors[compositeKey] = selected;
    this.emitSectorChange();
  }

  // Handler for industry changes on nginit
  onIndustryChangeInit(selected: any, fssVal: string) {
    if ((this.selectedIndustries[fssVal]?.length ?? 0) == 0) {
      this.selectedIndustries[fssVal] = selected;
    }
    else {
      if (this.selectedIndustries[fssVal].find(v => v.val == selected[0].val) == null) {
        this.selectedIndustries[fssVal].push(selected[0]);
      }
    }
    // Handle PEI Industry selection
    this.handlePEIIndustryAndSector(fssVal);
    this.emitIndustryChange();
  }

  // Handler for sector changes on nginit
  onSectorChangeInit(selected: any, industryVal: string, fssVal: string) {
    const sectorKey = industryVal + '_' + fssVal;
    if ((this.selectedSectors[sectorKey]?.length ?? 0) == 0) {
      this.selectedSectors[sectorKey] = selected;
    }
    else {
      if (this.selectedSectors[sectorKey].find(v => v.val == selected[0].val) == null) {
        this.selectedSectors[sectorKey].push(selected[0]);
      }
    }
    this.emitSectorChange();
  }

  // Remove FSS item
  removeFss(fssVal: string) {
    this.selectedFSS = this.selectedFSS.filter((fss) => fss.itemCode !== fssVal);
    this.selectedIndustries[fssVal]?.map((industry) => this.removeIndustry(fssVal, industry.val));
    this.emitFssChange();
  }

  // Remove industry item
  removeIndustry(fssVal: string, industryVal: string) {
    this.selectedIndustries[fssVal] = this.selectedIndustries[fssVal]?.filter((industry) => industry.val !== industryVal);
    this.selectedSectors[industryVal+'_'+fssVal]?.map((sector) => this.removeSector(fssVal + '_' + industryVal, sector.val));
    this.emitIndustryChange();
  }

  // Remove sector item
  removeSector(fssindustryVal: string, sectorVal: string) {
    this.selectedSectors[fssindustryVal] = this.selectedSectors[fssindustryVal]?.filter((sector) => sector.val !== sectorVal);
    this.emitSectorChange();
  }

  // Emit event for FSS change
  private emitFssChange() {
    const selectedFSSCodes = this.selectedFSS
      .map((fss: AdminNestedData) => fss.itemCode)
      .filter((code): code is string => code !== undefined);
    this.selectedFssCodesChange.emit(selectedFSSCodes);
  }

  // Emit event for industry change
  private emitIndustryChange() {
    let selectedIndustryCodes: string[] = [];
    selectedIndustryCodes = Object.values(this.selectedIndustries).reduce((acc: string[], industries: AdminNestedData[]) => {
      return acc.concat(industries.map((industry: AdminNestedData) => industry.val));
    }, []);
    this.selectedIndustryCodesChange.emit([...new Set(selectedIndustryCodes)]);
  }

  // Emit event for sector change
  private emitSectorChange() {
    let selectedSectorCodes: string[] = [];
    const nestedDetails: AdminNestedDataMap = {};

    // Process selected sectors
    this.selectedFSS.forEach((fss) => {
      if (fss.itemCode) {
        const industries = this.selectedIndustries[fss.itemCode] || [];
        nestedDetails[fss.itemCode] = { industries: {} };
        
        industries.forEach((industry) => {
          const compositeKey = industry.val + '_' + fss.itemCode; 
          const sectors = this.selectedSectors[compositeKey] || [];
          if (fss.itemCode && industry.val) {
            nestedDetails[fss.itemCode].industries[industry.val] = sectors.map((sector) => sector.val).filter(Boolean);
          }
          selectedSectorCodes.push(...sectors.map((sector) => sector.val).filter(Boolean));
        });
      }
    });

    // Emit event for sector change
    const industryAndSectorPerFSS = Object.entries(nestedDetails).map(([fssCode, data]) => ({
      fssCode,
      industries: Object.entries(data.industries).map(([industryCode, sectors]) => ({
        industryCode,
        sectors
      }))
    }));

    this.selectedSectorCodesChange.emit(industryAndSectorPerFSS);
  }
}

// Interface for nested data structure
export interface AdminNestedData {
  label: string;
  val: string;
  value?: boolean;
  itemId?: string;
  itemCode?: string;
  fssCode?: string;
}

export type AdminNestedDataMap = { [fssCode: string]: { industries: { [industryCode: string]: string[] } } };